/**
 * OfferModal Component - Create team offer/invitation
 *
 * Allows admins to send offers to players/parents for team enrollment.
 * Supports two-step process:
 * - Step 1: Registration (for new users)
 * - Step 2: Acceptance (after login)
 */

import React, { useState } from 'react';
import Modal from './Modal';
import { Grid, Col, FormInput, FormDatePicker, FormActionGroup } from './index';

const OfferModal = ({ isOpen, onClose, onSubmit, team, loading = false }) => {
  const [formData, setFormData] = useState({
    player_name: '',
    parent_name: '',
    parent_email: '',
    expires_at: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.player_name.trim()) {
      newErrors.player_name = 'Player name is required';
    }

    if (!formData.parent_name.trim()) {
      newErrors.parent_name = 'Parent name is required';
    }

    if (!formData.parent_email.trim()) {
      newErrors.parent_email = 'Parent email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.parent_email)) {
      newErrors.parent_email = 'Invalid email format';
    }

    if (!formData.expires_at) {
      newErrors.expires_at = 'Expiration date is required';
    } else {
      // Validate that expiration date is in the future
      const expiryDate = new Date(formData.expires_at);
      const now = new Date();
      if (expiryDate <= now) {
        newErrors.expires_at = 'Expiration date must be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Convert expires_at to ISO format with time at end of day
    const expiryDate = new Date(formData.expires_at);
    expiryDate.setHours(23, 59, 59, 999);

    const offerData = {
      ...formData,
      expires_at: expiryDate.toISOString()
    };

    try {
      await onSubmit(offerData);
      // Reset form on success
      setFormData({
        player_name: '',
        parent_name: '',
        parent_email: '',
        expires_at: ''
      });
      setErrors({});
    } catch (error) {
      // Error handling is done in parent component
      console.error('Error submitting offer:', error);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        player_name: '',
        parent_name: '',
        parent_email: '',
        expires_at: ''
      });
      setErrors({});
      onClose();
    }
  };

  // Get default expiration date (30 days from now)
  const getDefaultExpiryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Create Offer - ${team?.name || 'Team'}`}
      size="medium"
    >
      <form onSubmit={handleSubmit}>
        {/* Description */}
        <div style={{
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
          background: 'var(--surface-lvl1)',
          borderRadius: '8px',
          borderLeft: '3px solid var(--color-primary)'
        }}>
          <p style={{
            color: 'var(--color-text-secondary)',
            fontSize: '0.9rem',
            lineHeight: '1.6',
            margin: 0
          }}>
            Send an invitation to a player to join this team. The parent will receive an email
            to register (if new) and accept the offer.
          </p>
        </div>

        <Grid columns={1} gap="1.5rem">
          <Col>
            <FormInput
              label="Player Name"
              name="player_name"
              value={formData.player_name}
              onChange={handleChange}
              error={errors.player_name}
              placeholder="Enter player's full name"
              disabled={loading}
              required
            />
          </Col>

          <Col>
            <FormInput
              label="Parent/Guardian Name"
              name="parent_name"
              value={formData.parent_name}
              onChange={handleChange}
              error={errors.parent_name}
              placeholder="Enter parent/guardian's full name"
              disabled={loading}
              required
            />
          </Col>

          <Col>
            <FormInput
              label="Parent/Guardian Email"
              type="email"
              name="parent_email"
              value={formData.parent_email}
              onChange={handleChange}
              error={errors.parent_email}
              placeholder="parent@example.com"
              disabled={loading}
              required
            />
          </Col>

          <Col>
            <FormDatePicker
              label="Offer Expiration Date"
              name="expires_at"
              value={formData.expires_at}
              onChange={handleChange}
              error={errors.expires_at}
              min={new Date().toISOString().split('T')[0]}
              placeholder={getDefaultExpiryDate()}
              disabled={loading}
              helpText="The offer will expire at 11:59 PM on this date. Default is 30 days from today."
              required
            />
          </Col>
        </Grid>

        <FormActionGroup
          onSave={handleSubmit}
          onCancel={handleClose}
          saveLabel={loading ? 'Creating Offer...' : 'CREATE OFFER'}
          cancelLabel="CANCEL"
          loading={loading}
          isEdit={false}
        />
      </form>
    </Modal>
  );
};

export default OfferModal;
